import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchApi } from '../../services/apiBase';
import './CreatorMiniPanel.css';

interface CreatorMiniPanelProps {
  creatorName: string;
  anchorRect?: DOMRect | null;
  onClose: () => void;
}

const CreatorMiniPanel: React.FC<CreatorMiniPanelProps> = ({
  creatorName,
  anchorRect,
  onClose,
}) => {
  const navigate = useNavigate();
  const [planCount, setPlanCount] = useState<number | null>(null);
  const [destinations, setDestinations] = useState<string[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetchApi<{ itineraries: Array<{ id: number; destination?: string }>; total: number }>(
      `/api/planner/itineraries/?creator_name=${encodeURIComponent(creatorName)}&visibility=public&limit=10`
    )
      .then(data => {
        if (cancelled) return;
        setPlanCount(data.total ?? 0);
        // Extract unique destinations
        const dests = Array.from(new Set(
          (data.itineraries || []).map(p => p.destination).filter(Boolean) as string[]
        )).slice(0, 3);
        setDestinations(dests);
      })
      .catch(() => { if (!cancelled) setPlanCount(0); });
    return () => { cancelled = true; };
  }, [creatorName]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    setTimeout(() => document.addEventListener('click', handler), 0);
    return () => document.removeEventListener('click', handler);
  }, [onClose]);

  const initials = creatorName
    .split(' ')
    .map(w => w[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const [creatorUsername, setCreatorUsername] = useState<string | null>(null);

  // Try to fetch the creator's username for profile link
  useEffect(() => {
    let cancelled = false;
    fetchApi<{ username?: string; display_name?: string }>(
      `/auth/creators/${encodeURIComponent(creatorName)}/`
    )
      .then(data => { if (!cancelled && data.username) setCreatorUsername(data.username); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [creatorName]);

  const handleViewAll = () => {
    onClose();
    navigate(`/explore?creator=${encodeURIComponent(creatorName)}`);
  };

  const handleViewProfile = () => {
    onClose();
    if (creatorUsername) {
      navigate(`/@${creatorUsername}`);
    }
  };

  // Position popover near anchor
  const style: React.CSSProperties = {};
  if (anchorRect) {
    style.position = 'fixed';
    style.top = anchorRect.bottom + 8;
    style.left = Math.max(12, Math.min(anchorRect.left, window.innerWidth - 260));
    style.zIndex = 300;
  }

  return (
    <div ref={panelRef} className="creator-popover" style={style} onClick={e => e.stopPropagation()}>
      <div className="creator-popover-header">
        <div className="creator-popover-avatar">{initials}</div>
        <div>
          <div className="creator-popover-name">{creatorName}</div>
          {planCount !== null && (
            <div className="creator-popover-stat">{planCount} public plan{planCount !== 1 ? 's' : ''}</div>
          )}
          {destinations.length > 0 && (
            <div className="creator-popover-destinations">{destinations.join(' · ')}</div>
          )}
        </div>
      </div>
      <div className="creator-popover-actions">
        {creatorUsername && (
          <button className="creator-popover-action creator-popover-action--primary" onClick={handleViewProfile}>
            View profile
          </button>
        )}
        <button className="creator-popover-action" onClick={handleViewAll}>
          Filter plans in Explore
        </button>
      </div>
    </div>
  );
};

export default CreatorMiniPanel;
